import type { ChannelRow, ComplianceIssueType, CreateIssueInput } from '@qlk/shared';
import {
  COMPLIANCE_ISSUE_TYPES,
  COMPLIANCE_ISSUE_TYPE_LABEL,
  SUGGESTED_ACTIONS,
  SUGGESTED_ACTION_LABEL,
} from '@qlk/shared';
import { useId, useState, type FormEvent } from 'react';

import { Nut } from '../../../ui/index.js';

export interface FormSuCoProps {
  kenh: Pick<ChannelRow, 'id' | 'name'>[];
  dangGui: boolean;
  loi?: string | null;
  onGui: (input: CreateIssueInput) => void;
  onHuy: () => void;
}

const O_NHAP =
  'vung-bam w-full rounded-[10px] border border-vien bg-be-mat px-3 text-co-ban text-chu placeholder:text-chu-mo';

/** Form thêm sự cố nhập tay (YouTube không có API cho gậy và claim). */
export function FormSuCo({ kenh, dangGui, loi, onGui, onHuy }: FormSuCoProps) {
  const id = useId();
  const [channelId, setChannelId] = useState(kenh[0]?.id ?? '');
  const [videoTitle, setVideoTitle] = useState('');
  const [videoRef, setVideoRef] = useState('');
  const [type, setType] = useState<ComplianceIssueType>('content_id_claim');
  const [claimant, setClaimant] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [suggestedAction, setSuggestedAction] = useState('');

  function gui(e: FormEvent) {
    e.preventDefault();
    onGui({
      channelId,
      videoTitle: videoTitle.trim() || null,
      videoRef: videoRef.trim() || null,
      type,
      claimant: claimant.trim() || null,
      // Ô ngày của trình duyệt trả `YYYY-MM-DD`, lưu theo UTC đầu ngày.
      dueDate: dueDate ? new Date(`${dueDate}T00:00:00.000Z`).toISOString() : null,
      status: 'needs_response',
      suggestedAction: suggestedAction || null,
      assigneeId: null,
      source: 'manual',
    });
  }

  return (
    <form onSubmit={gui} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div>
        <label htmlFor={`${id}-kenh`} className="mb-1 block text-phu font-medium text-chu-phu">
          Kênh <span aria-hidden="true">*</span>
        </label>
        <select
          id={`${id}-kenh`}
          value={channelId}
          onChange={(e) => {
            setChannelId(e.target.value);
          }}
          className={O_NHAP}
        >
          {kenh.map((k) => (
            <option key={k.id} value={k.id}>
              {k.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`${id}-loai`} className="mb-1 block text-phu font-medium text-chu-phu">
          Loại sự cố
        </label>
        <select
          id={`${id}-loai`}
          value={type}
          onChange={(e) => {
            setType(e.target.value as ComplianceIssueType);
          }}
          className={O_NHAP}
        >
          {COMPLIANCE_ISSUE_TYPES.map((t) => (
            <option key={t} value={t}>
              {COMPLIANCE_ISSUE_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label htmlFor={`${id}-video`} className="mb-1 block text-phu font-medium text-chu-phu">
          Tên video
        </label>
        <input
          id={`${id}-video`}
          value={videoTitle}
          onChange={(e) => {
            setVideoTitle(e.target.value);
          }}
          className={O_NHAP}
          placeholder="Heavy rain on tin roof · 10h"
        />
      </div>

      <div>
        <label htmlFor={`${id}-ref`} className="mb-1 block text-phu font-medium text-chu-phu">
          Mã video trên YouTube
        </label>
        <input
          id={`${id}-ref`}
          value={videoRef}
          onChange={(e) => {
            setVideoRef(e.target.value);
          }}
          className={O_NHAP}
          placeholder="dQw4w9WgXcQ"
        />
      </div>

      <div>
        <label htmlFor={`${id}-claimant`} className="mb-1 block text-phu font-medium text-chu-phu">
          Bên khiếu nại
        </label>
        <input
          id={`${id}-claimant`}
          value={claimant}
          onChange={(e) => {
            setClaimant(e.target.value);
          }}
          className={O_NHAP}
          placeholder="Stock Footage Co."
        />
      </div>

      <div>
        <label htmlFor={`${id}-han`} className="mb-1 block text-phu font-medium text-chu-phu">
          Hạn phản hồi
        </label>
        <input
          id={`${id}-han`}
          type="date"
          value={dueDate}
          onChange={(e) => {
            setDueDate(e.target.value);
          }}
          className={O_NHAP}
        />
      </div>

      <div className="sm:col-span-2">
        <label htmlFor={`${id}-hanh-dong`} className="mb-1 block text-phu font-medium text-chu-phu">
          Hành động gợi ý
        </label>
        <select
          id={`${id}-hanh-dong`}
          value={suggestedAction}
          onChange={(e) => {
            setSuggestedAction(e.target.value);
          }}
          className={O_NHAP}
        >
          <option value="">Để hệ thống tự chọn theo loại sự cố</option>
          {SUGGESTED_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {SUGGESTED_ACTION_LABEL[a]}
            </option>
          ))}
        </select>
      </div>

      {loi && (
        <p role="alert" className="text-co-ban text-bad-chu sm:col-span-2">
          {loi}
        </p>
      )}

      <div className="flex justify-end gap-3 sm:col-span-2">
        <Nut kieu="phu" onClick={onHuy}>
          Huỷ
        </Nut>
        <Nut kieu="chinh" type="submit" dangChay={dangGui}>
          Thêm sự cố
        </Nut>
      </div>
    </form>
  );
}
